import React from 'react';
import { Badge, BadgeType, formatDate, Tooltip, ItemPagination } from '@enter/core';
import { ProviderEvidence } from '@enter/core';
import Icon from '@mdi/react';
import { mdiContentCopy, mdiShare, mdiUpdate, mdiCheckCircleOutline, mdiAlertCircleOutline } from '@mdi/js';
import * as S from './evidence-header.styles';

interface EvidenceHeaderProps {
    evidence: ProviderEvidence;
    onCopyEvidenceId: () => void;
    onCopyEvidenceUrl: () => void;
    // Pagination props (optional - only shown when navigating from list)
    paginationOffset?: number;
    paginationTotal?: number;
    onPrevious?: () => void;
    onNext?: () => void;
    isNavigating?: boolean;
}

const getEvidenceStatusBadgeType = (status: string): BadgeType => {
    switch (status) {
        case "SUBMITTED":
            return BadgeType.ORANGE;
        case "APPROVED":
            return BadgeType.GREEN;
        case "DENIED":
            return BadgeType.RED;
        default:
            return BadgeType.BLUE_LIGHT;
    }
};

export const EvidenceHeader: React.FC<EvidenceHeaderProps> = ({
    evidence,
    onCopyEvidenceId,
    onCopyEvidenceUrl,
    paginationOffset,
    paginationTotal,
    onPrevious,
    onNext,
    isNavigating = false,
}) => {
    const hasPagination = paginationOffset !== undefined && paginationTotal !== undefined && onPrevious && onNext;

    return (
        <S.HeaderContainer>
            {/* Title and Action Buttons */}
            <S.TitleRow>
                <S.TitleGroup>
                    <S.Title>Evidence Details</S.Title>
                    
                    {hasPagination && (
                        <ItemPagination
                            currentIndex={paginationOffset}
                            total={paginationTotal}
                            onPrevious={onPrevious}
                            onNext={onNext}
                            isNavigating={isNavigating}
                            size="small"
                        />
                    )}
                </S.TitleGroup>
                <S.ActionsGroup>
                    {/* Copy Evidence ID Button */}
                    <Tooltip
                        direction="top"
                        variant="dark"
                        title={<span>Copy Evidence ID</span>}>
                        <S.ActionButton onClick={onCopyEvidenceId}>
                            <Icon path={mdiContentCopy} size={0.8} />
                        </S.ActionButton>
                    </Tooltip>

                    {/* Copy Evidence URL Button */}
                    <Tooltip
                        direction="top"
                        variant="dark"
                        title={<span>Copy Evidence URL</span>}>
                        <S.ActionButton onClick={onCopyEvidenceUrl}>
                            <Icon path={mdiShare} size={0.8} />
                        </S.ActionButton>
                    </Tooltip>
                </S.ActionsGroup>
            </S.TitleRow>

            {/* Status and Evidence Information as Badges */}
            <S.BadgesContainer>
                {/* Status Badge */}
                <Tooltip
                    direction="top"
                    variant="dark"
                    title={<span>Evidence Status: {evidence.status || 'N/A'}</span>}>
                    <Badge type={getEvidenceStatusBadgeType(evidence.status || '')} size="small">
                        <S.BadgeContent>
                            {evidence.status === 'SUBMITTED' && (
                                <Icon path={mdiUpdate} size={0.7} />
                            )}
                            {evidence.status === 'APPROVED' && (
                                <Icon path={mdiCheckCircleOutline} size={0.7} />
                            )}
                            {evidence.status === 'DENIED' && (
                                <Icon path={mdiAlertCircleOutline} size={0.7} />
                            )}
                            <span>{evidence.status || 'N/A'}</span>
                        </S.BadgeContent>
                    </Badge>
                </Tooltip>

                {/* Updated Badge */}
                {evidence.updated && (
                    <Tooltip
                        direction="top"
                        variant="dark"
                        title={<span>Last Updated</span>}>
                        <Badge type={BadgeType.BLUE_LIGHT} size="small">
                            Updated: {formatDate(evidence.updated, 'MMM d, yyyy')}
                        </Badge>
                    </Tooltip>
                )}

                {/* Expires Badge */}
                {evidence.expired && (
                    <Tooltip
                        direction="top"
                        variant="dark"
                        title={<span>Expiration Date</span>}>
                        <Badge type={BadgeType.BLUE_LIGHT} size="small">
                            Expires: {formatDate(evidence.expired, 'MMM d, yyyy')}
                        </Badge>
                    </Tooltip>
                )}
            </S.BadgesContainer>
        </S.HeaderContainer>
    );
};

